import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { api } from "../../services/api";

const SubscriptionPlanDistributionChart = () => {
  const [planData, setPlanData] = useState({
    planCounts: {},
    totalSubscriptions: 0,
    loading: true,
    error: false,
  });

  // Chart series and options
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  useEffect(() => {
    const fetchPlanDistribution = async () => {
      try {
        // Fetch revenue stats from API
        const response = await api.subscriptions.getRevenueStats();

        if (response.success && response.data) {
          const { totalRevenue } = response.data;

          if (totalRevenue && totalRevenue.planCounts) {
            // Extract plan counts
            const planCounts = totalRevenue.planCounts;

            // Calculate total subscriptions
            const totalSubscriptions = Object.values(planCounts).reduce(
              (sum, count) => sum + count,
              0
            );

            // Prepare chart series data - only include plans with subscribers
            const series = [];
            const labels = [];

            Object.entries(planCounts).forEach(([plan, count]) => {
              if (count > 0) {
                series.push(count);
                labels.push(plan);
              }
            });

            // Update chart data
            setChartSeries(series);
            setChartLabels(labels);

            // Update state
            setPlanData({
              planCounts,
              totalSubscriptions,
              loading: false,
              error: false,
            });
          } else {
            throw new Error("Plan distribution data not available");
          }
        } else {
          throw new Error(
            response.message || "Failed to fetch plan distribution data"
          );
        }
      } catch (error) {
        console.error("Error fetching plan distribution data:", error);

        // Set fallback data
        const fallbackData = {
          FREE: 10,
          BASIC: 25,
          PRO: 50,
          ENTERPRISE: 15,
        };

        setChartSeries(Object.values(fallbackData));
        setChartLabels(Object.keys(fallbackData));

        setPlanData({
          planCounts: fallbackData,
          totalSubscriptions: 100,
          loading: false,
          error: true,
        });
      }
    };

    fetchPlanDistribution();
  }, []);

  // Chart options
  const chartOptions = {
    chart: {
      type: "donut",
      height: 320,
    },
    colors: ["#45B369", "#487FFF", "#FF9F29", "#FF6A49"],
    labels: chartLabels,
    legend: {
      position: "bottom",
      horizontalAlign: "center",
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "16px",
              fontWeight: 600,
            },
            value: {
              show: true,
              fontSize: "14px",
              formatter: function (val) {
                return val;
              },
            },
            total: {
              show: true,
              label: "Total",
              formatter: function () {
                return planData.totalSubscriptions;
              },
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 300,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
    tooltip: {
      y: {
        formatter: function (value) {
          return value + " subscribers";
        },
      },
    },
  };

  // Render plan counts as a horizontal bar graph
  const renderPlanBars = () => {
    if (!planData.planCounts || Object.keys(planData.planCounts).length === 0) {
      return null;
    }

    const planColors = {
      FREE: "#45B369",
      BASIC: "#487FFF",
      PRO: "#FF9F29",
      ENTERPRISE: "#FF6A49",
    };

    return (
      <div className="mt-3">
        {Object.entries(planData.planCounts).map(([plan, count]) => (
          <div key={plan} className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <div className="fw-medium">{plan}</div>
              <div className="fw-semibold">{count}</div>
            </div>
            <div className="progress" style={{ height: "10px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${(count / planData.totalSubscriptions) * 100}%`,
                  backgroundColor: planColors[plan] || "#6c757d",
                }}
                aria-valuenow={count}
                aria-valuemin="0"
                aria-valuemax={planData.totalSubscriptions}
              ></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="col-xxl-6 col-xl-6">
      <div className="card h-100 radius-8 border">
        <div className="card-body p-24">
          <h6 className="mb-16 fw-semibold text-lg">
            Subscription Plan Distribution
          </h6>

          {planData.error && (
            <div className="text-sm text-danger-main mb-2">
              <Icon icon="mdi:alert-circle-outline" className="me-1" />
              Using demo data (API error)
            </div>
          )}

          {planData.loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="row">
                <div className="col-lg-7">{renderPlanBars()}</div>
                <div className="col-lg-5">
                  {chartSeries.length > 0 ? (
                    <ReactApexChart
                      options={chartOptions}
                      series={chartSeries}
                      type="donut"
                      height={240}
                    />
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No subscription data available
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlanDistributionChart;
